import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useCallback, useEffect } from "react";
import {
  PreloadedQuery,
  RelayEnvironmentProvider,
  usePreloadedQuery,
  useQueryLoader,
  useRelayEnvironment,
} from "react-relay";

import { Loader } from "@whoosh/design-system/src/components/display";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { Text, VStack } from "@whoosh/design-system/src/components/primitive";

import { RainchecksList } from "../RainchecksList/RainchecksList";
import { RainchecksModalQuery } from "./__generated__/RainchecksModalQuery.graphql";

type Props = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSelectRaincheck?: (raincheckId: string) => void;
  isLoading?: boolean;
  appliedRaincheckIds?: string[];
  redeemableAtFacilityId?: string | null;
  activeOnly?: boolean;
  showTotalBalance?: boolean;
};

export const RainchecksModal = ({
  isOpen,
  setIsOpen,
  onSelectRaincheck,
  isLoading,
  appliedRaincheckIds,
  redeemableAtFacilityId,
  activeOnly,
  showTotalBalance,
}: Props) => {
  const [queryRef, loadQuery] = useQueryLoader<RainchecksModalQuery>(
    RainchecksModalQueryString
  );

  const environment = useRelayEnvironment();

  useEffect(() => {
    if (isOpen) {
      loadQuery({ redeemableAtFacilityId }, { fetchPolicy: "network-only" });
    }
  }, [loadQuery, isOpen, redeemableAtFacilityId]);

  const refetchQuery = useCallback(() => {
    loadQuery({ redeemableAtFacilityId }, { fetchPolicy: "network-only" });
  }, [loadQuery, redeemableAtFacilityId]);

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      upperTitle={showTotalBalance ? undefined : t`Rainchecks`}
      hideFooterPadding
      content={
        <RelayEnvironmentProvider environment={environment}>
          <Suspense
            fallback={
              <RainchecksModalLoadingState showTitle={showTotalBalance} />
            }
          >
            {isLoading ? <Loader /> : null}
            {queryRef ? (
              <RainchecksModalInner
                queryRef={queryRef}
                refetchQuery={refetchQuery}
                onSelectRaincheck={onSelectRaincheck}
                appliedRaincheckIds={appliedRaincheckIds}
                activeOnly={activeOnly}
                showTotalBalance={showTotalBalance}
              />
            ) : (
              <RainchecksModalLoadingState showTitle={showTotalBalance} />
            )}
          </Suspense>
        </RelayEnvironmentProvider>
      }
    />
  );
};

const RainchecksModalLoadingState = ({
  showTitle,
}: {
  showTitle?: boolean;
}) => {
  if (!showTitle) {
    return <Loader />;
  }

  return (
    <VStack gap={6} pb={4} px={6}>
      <Text pt={8} variant="mainTitle2">
        {t`Rainchecks`}
      </Text>
      <Loader />
    </VStack>
  );
};

const RainchecksModalInner = ({
  queryRef,
  onSelectRaincheck,
  appliedRaincheckIds,
  activeOnly,
  showTotalBalance,
}: {
  queryRef: PreloadedQuery<RainchecksModalQuery>;
  refetchQuery: () => void;
  onSelectRaincheck?: (raincheckId: string) => void;
  appliedRaincheckIds?: string[];
  activeOnly?: boolean;
  showTotalBalance?: boolean;
}) => {
  const data = usePreloadedQuery(RainchecksModalQueryString, queryRef);

  return (
    <RainchecksList
      fragmentRef={data?.session?.currentClientProfile?.member ?? null}
      onSelectRaincheck={onSelectRaincheck}
      appliedRaincheckIds={appliedRaincheckIds}
      activeOnly={activeOnly}
      showTotalBalance={showTotalBalance}
    />
  );
};

export const RainchecksModalQueryString = graphql`
  query RainchecksModalQuery($redeemableAtFacilityId: ID) {
    session {
      currentClientProfile {
        member {
          ... on Customer {
            ...RainchecksList_customer
              @arguments(redeemableAtFacilityId: $redeemableAtFacilityId)
            rainchecks {
              edges {
                node {
                  id
                  squareGiftCard {
                    id
                    balance
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
